import React from 'react';
import './ListRow.css';

export interface ListRowProps extends React.HTMLAttributes<HTMLDivElement> {
  topLeft?: React.ReactNode;
  topRight?: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  meta?: React.ReactNode;
  onClick?: () => void;
  density?: 'compact' | 'default' | 'comfortable';
  surface?: 'list' | 'card';
}

export const ListRow: React.FC<ListRowProps> = ({
  topLeft,
  topRight,
  title,
  subtitle,
  meta,
  onClick,
  density = 'default',
  surface = 'list',
  className = '',
  onKeyDown,
  role,
  tabIndex,
  ...props
}) => {
  const hasTitle = !(title === null || title === undefined || title === '');
  const hasSubtitle = !(subtitle === null || subtitle === undefined || subtitle === '');
  const hasMeta = !(meta === null || meta === undefined || meta === '');
  const classNames = [
    'ui-list-row',
    `ui-list-row--${density}`,
    `ui-list-row--${surface}`,
    onClick && 'ui-list-row--clickable',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <>
      {(topLeft || topRight) && (
        <div className="ui-list-row__topline">
          <div className="ui-list-row__topline-start">{topLeft}</div>
          {topRight ? <div className="ui-list-row__topline-end">{topRight}</div> : null}
        </div>
      )}

      <div className="ui-list-row__content">
        {hasTitle ? <div className="ui-list-row__title">{title}</div> : null}
        {hasSubtitle ? <div className="ui-list-row__subtitle">{subtitle}</div> : null}
        {hasMeta ? <div className="ui-list-row__meta">{meta}</div> : null}
      </div>
    </>
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented || !onClick) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className={classNames}
      role={onClick ? role || 'button' : role}
      tabIndex={onClick ? tabIndex ?? 0 : tabIndex}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      {...props}
    >
      {content}
    </div>
  );
};

ListRow.displayName = 'ListRow';
